import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, Eyebrow } from "@/components/admin/ui";
import { Loader2, ShieldCheck } from "lucide-react";

const Field = ({ label, hint, ...rest }) => (
  <div>
    <Label className="text-neutral-400 text-[10px] uppercase tracking-widest">{label}</Label>
    <Input
      {...rest}
      className="mt-1.5 bg-neutral-900 border-neutral-800 h-11 focus-visible:ring-yellow-300"
    />
    {hint && <div className="text-[10.5px] text-white/30 mt-1">{hint}</div>}
  </div>
);

export function FeesSettingsCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qrBusy, setQrBusy] = useState(false);

  const uploadQr = async (file) => {
    if (!file) return;
    setQrBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/payment-settings/qr", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setS((prev) => ({ ...prev, qr_url: data.qr_url }));
      toast.success("Your QR is now used in the checkout");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setQrBusy(false);
    }
  };

  const clearQr = async () => {
    try {
      await api.delete("/payment-settings/qr");
      setS((prev) => ({ ...prev, qr_url: "" }));
      toast.success("Back to the generated QR (amount pre-filled)");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    api
      .get("/payment-settings")
      .then(({ data }) => setS(data))
      .catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/payment-settings", {
        upi_vpa: s.upi_vpa || "",
        upi_name: s.upi_name || "",
        whatsapp_number: s.whatsapp_number || "",
        course_name: s.course_name || "",
        fee_amount: Number(s.fee_amount) || 499,
        anchor_amount: Number(s.anchor_amount) || 0,
        token_amount: Number(s.token_amount) || 49,
        token_enabled: !!s.token_enabled,
        hold_hours: Number(s.hold_hours) || 24,
      });
      setS(data);
      toast.success("Fees setup saved — the in-class checkout is live");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return null;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  return (
    <Card className="p-5" testid="settings-fees">
      <Eyebrow className="mb-1">Fees & UPI collection</Eyebrow>
      <p className="text-[12px] text-white/40 mb-4">
        Students pay straight into your UPI ID from inside the class — no gateway, no 2.6% cut. The
        last two digits of the amount are their seat number, so a screenshot maps to exactly one
        student.
      </p>

      <form onSubmit={save} className="grid sm:grid-cols-2 gap-3">
        <Field
          label="Your UPI ID (VPA)"
          value={s.upi_vpa || ""}
          onChange={set("upi_vpa")}
          placeholder="udyogclasses@okhdfcbank"
          hint="Money lands here directly"
          data-testid="fees-vpa"
        />
        <Field
          label="Name shown in their UPI app"
          value={s.upi_name || ""}
          onChange={set("upi_name")}
          placeholder="Udyog Classes"
          data-testid="fees-upi-name"
        />
        <Field
          label="WhatsApp number for screenshots"
          value={s.whatsapp_number || ""}
          onChange={(e) => setS({ ...s, whatsapp_number: e.target.value.replace(/\D/g, "").slice(0, 10) })}
          placeholder="9876543210"
          inputMode="numeric"
          hint="Students message this number to send proof and lock seats"
          data-testid="fees-whatsapp"
        />
        <Field
          label="Course name on the checkout"
          value={s.course_name || ""}
          onChange={set("course_name")}
          placeholder="Complete Course 2026"
          data-testid="fees-course"
        />
        <Field
          label="Full fee (₹)"
          value={s.fee_amount ?? ""}
          onChange={(e) => setS({ ...s, fee_amount: e.target.value.replace(/\D/g, "").slice(0, 6) })}
          inputMode="numeric"
          data-testid="fees-amount"
        />
        <Field
          label="Compare-at price (₹)"
          value={s.anchor_amount ?? ""}
          onChange={(e) => setS({ ...s, anchor_amount: e.target.value.replace(/\D/g, "").slice(0, 7) })}
          inputMode="numeric"
          hint="Shown first, then struck out and shrunk beside the real fee. 0 hides it"
          data-testid="fees-anchor"
        />
        <Field
          label="Seat booking amount (₹)"
          value={s.token_amount ?? ""}
          onChange={(e) => setS({ ...s, token_amount: e.target.value.replace(/\D/g, "").slice(0, 6) })}
          inputMode="numeric"
          hint="A smaller yes beats a big maybe"
          data-testid="fees-token"
        />
        <Field
          label="Hold a reserved seat for (hours)"
          value={s.hold_hours ?? ""}
          onChange={(e) => setS({ ...s, hold_hours: e.target.value.replace(/\D/g, "").slice(0, 3) })}
          inputMode="numeric"
          data-testid="fees-hold"
        />
        <div className="flex items-end">
          <button
            type="button"
            onClick={() => setS({ ...s, token_enabled: !s.token_enabled })}
            className={`h-11 w-full rounded-full text-[10.5px] uppercase tracking-[0.14em] transition-colors ${
              s.token_enabled
                ? "bg-yellow-300 text-black font-semibold"
                : "ring-1 ring-white/10 text-white/45 hover:text-white"
            }`}
            data-testid="fees-token-toggle"
          >
            {s.token_enabled ? "✓ Seat booking offered" : "Seat booking hidden"}
          </button>
        </div>

        <div className="sm:col-span-2 mt-1 p-4 rounded-2xl ring-1 ring-white/[0.07] bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400">
            Your own QR code (optional)
          </div>
          <p className="text-[11.5px] text-white/35 mt-1 mb-3">
            Leave this empty and we generate a QR that already carries the exact amount. Upload your
            bank's QR only if you prefer it — a bank QR has no amount in it, so the checkout will ask
            the student to type the exact figure.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            {s.qr_url && (
              <img
                src={s.qr_url}
                alt="Your QR"
                className="w-16 h-16 rounded-lg object-contain bg-white p-1"
                data-testid="fees-qr-preview"
              />
            )}
            <label className="h-10 px-4 rounded-full ring-1 ring-white/[0.12] text-[10.5px] uppercase tracking-[0.12em] text-white/60 hover:text-white inline-flex items-center cursor-pointer">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => uploadQr(e.target.files?.[0])}
                data-testid="fees-qr-input"
              />
              {qrBusy ? "Uploading…" : s.qr_url ? "Replace QR" : "Upload QR image"}
            </label>
            {s.qr_url && (
              <button
                type="button"
                onClick={clearQr}
                className="h-10 px-4 rounded-full ring-1 ring-white/[0.12] text-[10.5px] uppercase tracking-[0.12em] text-white/40 hover:text-white"
                data-testid="fees-qr-clear"
              >
                Use the generated QR
              </button>
            )}
          </div>
        </div>

        <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
          <Button
            type="submit"
            disabled={saving}
            className="rounded-full bg-yellow-300 hover:bg-yellow-200 text-black font-semibold h-11"
            data-testid="fees-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save fees setup"}
          </Button>
          <span
            className={`text-[10.5px] uppercase tracking-[0.14em] inline-flex items-center gap-1.5 ${
              s.ready ? "text-emerald-300" : "text-amber-300"
            }`}
            data-testid="fees-ready"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {s.ready ? "Checkout is live" : "Add your UPI ID and WhatsApp number to go live"}
          </span>
        </div>
      </form>
    </Card>
  );
}
